
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Team } from '@/types/team';

interface ValidationErrors {
  teamName?: string;
  selectedClubId?: string;
  selectedSeasonId?: string;
}

export const useTeamForm = (clubId?: string, onSuccess?: () => void) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [teamName, setTeamName] = useState('');
  const [selectedClubId, setSelectedClubId] = useState(clubId || '');
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [selectedCoachId, setSelectedCoachId] = useState('');
  const [selectedDivisionId, setSelectedDivisionId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    
    if (!teamName.trim()) {
      errors.teamName = 'Team name is required';
    }
    
    if (!selectedClubId) {
      errors.selectedClubId = 'Please select a club';
    }
    
    if (!selectedSeasonId) {
      errors.selectedSeasonId = 'Please select a season';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const clearValidationError = (field: keyof ValidationErrors) => {
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      // Convert placeholder values back to null for database
      const divisionIdValue = (selectedDivisionId && selectedDivisionId !== 'no-division' && selectedDivisionId !== '') ? selectedDivisionId : null;
      
      const teamData = {
        name: teamName,
        club_id: selectedClubId,
        season_id: selectedSeasonId,
        division_id: divisionIdValue
      };


      if (editingTeam) {
        const { data, error } = await supabase
          .from('teams')
          .update(teamData)
          .eq('id', editingTeam.id)
          .select();

        if (error) {
          throw error;
        }
        toast.success('Team updated successfully');
      } else {
        const { data, error } = await supabase
          .from('teams')
          .insert([teamData])
          .select();

        if (error) {
          throw error;
        }
        toast.success('Team created successfully');
      }

      handleDialogClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error saving team:', error);
      toast.error('Failed to save team');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (team: Team) => {
    setEditingTeam(team);
    setTeamName(team.name);
    setSelectedClubId(team.club_id);
    setSelectedSeasonId(team.season_id || '');
    
    // Use placeholder value for UI, convert null/undefined to 'no-division'
    const divisionId = team.division_id || 'no-division';
    setSelectedDivisionId(divisionId);

    // Clear validation errors when editing
    setValidationErrors({});
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingTeam(null);
    setTeamName('');
    setSelectedClubId(clubId || '');
    setSelectedSeasonId('');
    setSelectedCoachId('');
    setSelectedDivisionId('');
    setValidationErrors({});
  };

  return {
    isDialogOpen,
    setIsDialogOpen,
    editingTeam,
    teamName,
    setTeamName: (value: string) => {
      setTeamName(value);
      if (value.trim()) clearValidationError('teamName');
    },
    selectedClubId,
    setSelectedClubId: (value: string) => {
      setSelectedClubId(value);
      if (value) clearValidationError('selectedClubId');
    },
    selectedSeasonId,
    setSelectedSeasonId: (value: string) => {
      setSelectedSeasonId(value);
      if (value) clearValidationError('selectedSeasonId');
    },
    selectedCoachId,
    setSelectedCoachId,
    selectedDivisionId,
    setSelectedDivisionId,
    isSubmitting,
    validationErrors,
    handleSubmit,
    handleEdit,
    handleDialogClose
  };
};
